import React, { Fragment } from "react";
import { withTheme, withStyles } from "@material-ui/core/styles";
import { injectIntl } from "react-intl";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextareaAutosize,
  Tooltip,
} from "@material-ui/core";
import { withHistory, formatMessage, FormPanel } from "@openimis/fe-core";
import { withModulesManager, journalize, formatDateFromISO } from "@openimis/fe-core";
import { DETAIL_IN_REVIEW, DETAIL_DATA_COLLECTION_IN_PROGRESS, DETAIL_FORM_DEPLOYED } from "../constants";
import { fetchAccreditationDetailForms } from "../actions";

const styles = (theme) => ({
  tableTitle: theme.table.title,
  page: theme.page,
  paper: theme.paper.paper,
  item: theme.paper.item,
  title: theme.paper.title,
  fullHeight: {
    height: "100%",
  },
  rejectDialog: {
    padding: theme.spacing(2),
  },
  commentBox: {
    width: "100%",
    minWidth: 400,
    minHeight: 120,
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontFamily: theme.typography.fontFamily,
    fontSize: "1rem",
    border: "1px solid #ccc",
    borderRadius: "4px",
    resize: "vertical",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  actionButton: {
    minWidth: 100,
  },
});

// Main component class to manage accreditation lifecycle
class AccreditationMasterPanel extends FormPanel {
  state = {
    rejectModalOpen: false,
    rejectionComment: "",
    isProcessing: false,
    isApproved: false,
    validatedForms: [],
    forms: [],
    validating: false,
    validationSuccess: false,
    validationError: null,
    selectedForm: null,
    hasValidatedForms: false, // Indicates whether all forms have been validated
  };

  // Fetch detail forms when the component mounts
  componentDidMount() {
    if (this.props.edited && this.props.edited[0]?.uuid) {
      this.props.fetchAccreditationDetailForms(this.props.edited[0].uuid);
    }
    this.checkValidatedForms();
  }

  // Refetch forms or recalculate when edited or form data changes
  componentDidUpdate(prevProps) {
    if (
      prevProps.accreditationDetailsForms !== this.props.accreditationDetailsForms ||
      prevProps.edited !== this.props.edited
    ) {
      const { accreditationDetailsForms } = this.props;
      this.setState({ forms: accreditationDetailsForms || [] }, this.checkValidatedForms);
    }
  }

  // Validates if all forms related to current accreditation have submitted data
  checkValidatedForms = () => {
    const { edited, validatedForms } = this.props;
    const { forms } = this.state;
    if (!edited || !edited[0]) return;

    const accreditationId = edited[0].id;

    // All published forms related to this accreditation
    const publishedForms = forms.map((form) => ({
      ...form,
      accreditationId,
    }));

    // Only allow approval if all forms have `submitedData === true`

    const hasValidatedForms = publishedForms.length > 0 && publishedForms.every((form) => form.submitedData === true);

    this.setState({ hasValidatedForms });
  };
  openRejectModal = () => this.setState({ rejectModalOpen: true });
  closeRejectModal = () => this.setState({ rejectModalOpen: false, rejectionComment: "" });

  // Updates state as user types comment in rejection box
  handleCommentChange = (e) => this.setState({ rejectionComment: e.target.value });

  // Handles approval or rejection of an accreditation
  handleApproveOrReject = (action, comments) => {
    const { edited, approveOrDeclineAccreditationDetail } = this.props;
    if (!edited || !edited[0]) return;

    this.setState({ isProcessing: true });

    let status,
      declineType = "";
    if (action === "approve") {
      status = "AP";
      this.setState({ isApproved: true });
    } else if (action === "probation") {
      status = "DC";
      declineType = "probation";
    } else if (action === "failed") {
      status = "DC";
      declineType = "failed";
    }

    const payload = {
      uuids: [edited[0].uuid],
      status,
      declineType,
      declineReason: "",
      comments: comments || "",
      clientMutationLabel: `${action === "approve" ? "Approved" : "Rejected"} Accreditation Detail`,
    };

    approveOrDeclineAccreditationDetail(payload)
      .then(() => setTimeout(() => window.location.reload(), 1500))
      .catch((error) => {
        console.error("Approval error:", error);
        this.setState({ isProcessing: false });
      });
  };

  // Starts or stops data collection state
  toggleAccreditationStatus = (action) => {
    const { startOrStopAccreditation } = this.props;
    startOrStopAccreditation(action);
    setTimeout(() => window.location.reload(), 1000);
  };

  // Calculates 'valid till' date (2 years after start date)
  calculateValidTill = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    date.setFullYear(date.getFullYear() + 2);
    return formatDateFromISO(this.props.modulesManager, this.props.intl, date.toISOString());
  };

  // Click handlers for Approve, Probation, and Failed actions
  handleApproveClick = () => this.handleApproveOrReject("approve", "Approved Accreditation Detail");
  handleProbationClick = () => {
    this.handleApproveOrReject("probation", "Rejected Accreditation Detail");
    this.closeRejectModal();
  };
  handleFailedClick = () => {
    this.handleApproveOrReject("failed", "Rejected Accreditation Detail");
    this.closeRejectModal();
  };

  render() {
    const { classes, updateAttribute, edited, initiateAccreditationDetail, validatedForms } = this.props;
    const { rejectModalOpen, rejectionComment, isProcessing, isApproved, forms } = this.state;

    const isAlreadyApproved = edited && edited[0]?.status === "AP";
    const showValidTill = isApproved || isAlreadyApproved;
    const canApprove = !isAlreadyApproved && this.state.hasValidatedForms && !isProcessing;

    return (
      <Fragment>
        <Paper className={classes.paper}>
          <Typography className={classes.title}>Accreditation Details</Typography>
          <div className={classes.page}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Accreditation Name"
                  value={edited ? edited[0]?.projectName : ""}
                  onChange={(e) => updateAttribute("projectName", e.target.value)}
                  disabled={!!edited || isProcessing}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="State"
                  value={edited ? formatMessage(this.props.intl, "accreditation", `state.${edited[0]?.state}`) : ""}
                  disabled
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Status"
                  value={
                    edited
                      ? formatMessage(
                          this.props.intl,
                          "accreditation",
                          `quality_assurance_and_accreditation.status.${edited[0]?.status}`,
                        )
                      : ""
                  }
                  disabled
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Date Started"
                  value={
                    edited ? formatDateFromISO(this.props.modulesManager, this.props.intl, edited[0]?.created) : ""
                  }
                  disabled
                />
              </Grid>
              {showValidTill && (
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Valid Till"
                    value={edited && edited[0]?.created ? this.calculateValidTill(edited[0].created) : ""}
                    disabled
                  />
                </Grid>
              )}
            </Grid>

            {edited && edited[0] && !isProcessing && (
              <Box display="flex" justifyContent="end" mt={2}>
                {edited[0].state === DETAIL_FORM_DEPLOYED && (
                  <Box mx={2}>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={() => this.toggleAccreditationStatus("start")}
                      disabled={isProcessing}
                    >
                      Start Data Collection
                    </Button>
                  </Box>
                )}
                {edited[0].state === DETAIL_DATA_COLLECTION_IN_PROGRESS && (
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => this.toggleAccreditationStatus("stop")}
                    disabled={isProcessing}
                  >
                    Stop Data Collection
                  </Button>
                )}
              </Box>
            )}

            {edited && !edited[0]?.isInitialized && !isProcessing && (
              <Box display="flex" justifyContent="end" mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    initiateAccreditationDetail();
                    window.location.reload();
                  }}
                  disabled={isProcessing}
                >
                  Initiate Accreditation
                </Button>
              </Box>
            )}

            {edited && edited[0]?.state === DETAIL_IN_REVIEW && !isAlreadyApproved && (
              <Box display="flex" justifyContent="end" mt={2}>
                <Box mx={2}>
                  <Tooltip
                    title={
                      !this.state.hasValidatedForms ? "You must validate all available forms before approving" : ""
                    }
                    placement="top"
                  >
                    <span>
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={this.handleApproveClick}
                        disabled={!canApprove}
                      >
                        {isProcessing ? "Processing..." : "Approve"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <Button color="error" variant="outlined" onClick={this.openRejectModal} disabled={isProcessing}>
                  Reject
                </Button>
              </Box>
            )}
          </div>
        </Paper>

        <Dialog open={rejectModalOpen} onClose={this.closeRejectModal} maxWidth="sm">
          <DialogTitle>Reject Accreditation</DialogTitle>
          <DialogContent className={classes.rejectDialog}>
            <Typography variant="body1">Please provide a reason for rejection:</Typography>
            <TextareaAutosize
              className={classes.commentBox}
              minRows={4}
              placeholder="Enter comment here..."
              value={rejectionComment}
              onChange={this.handleCommentChange}
              disabled={isProcessing}
            />
          </DialogContent>
          <DialogActions className={classes.buttonContainer}>
            <Button variant="contained" color="primary" onClick={this.handleProbationClick} disabled={isProcessing}>
              {isProcessing ? "Processing..." : "Probation"}
            </Button>
            <Button variant="contained" color="primary" onClick={this.handleFailedClick} disabled={isProcessing}>
              {isProcessing ? "Processing..." : "Failed"}
            </Button>
          </DialogActions>
        </Dialog>
      </Fragment>
    );
  }
}

// Redux mappings to props
const mapStateToProps = (state) => ({
  rights: state.core?.user?.i_user?.rights || [],
  mutation: state.core.mutation,
  validatedForms: state.quality_assurance_and_accreditation?.validatedForms || [],
  fetchedAllForms: state.quality_assurance_and_accreditation.fetchedAllForms || [],
  allForms: state.quality_assurance_and_accreditation.allForms || [],
  forms: state.quality_assurance_and_accreditation.forms || [],
  accreditationDetailsForms: state.quality_assurance_and_accreditation.accreditationDetailsForms,
  fetchingAccreditationDetailsForms: state.quality_assurance_and_accreditation.fetchingAccreditationDetailsForms,
  fetchedAccreditationDetailsForms: state.quality_assurance_and_accreditation.fetchedAccreditationDetailsForms,
  errorAccreditationDetailsForms: state.quality_assurance_and_accreditation.errorAccreditationDetailsForms,
  qaDetailsForms: state.quality_assurance_and_accreditation.qaDetailsForms,
  fetchingQADetailsForms: state.quality_assurance_and_accreditation.fetchingQADetailsForms,
  fetchedQADetailsForms: state.quality_assurance_and_accreditation.fetchedQADetailsForms,
  errorQADetailsForms: state.quality_assurance_and_accreditation.errorQADetailsForms,
  accreditationDetailsFormData: state.quality_assurance_and_accreditation.accreditationDetailsFormData,
  qaDetailsFormData: state.quality_assurance_and_accreditation.qaDetailsFormData,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ journalize, fetchAccreditationDetailForms }, dispatch);

// Exporting wrapped component with HOCs
export default withModulesManager(
  injectIntl(
    withTheme(withStyles(styles)(withHistory(connect(mapStateToProps, mapDispatchToProps)(AccreditationMasterPanel)))),
  ),
);
